import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
// Import Types
import type { ApprovalRequest } from 'types';
// Import UI Components
import { Tag } from '@geneui/components';
// Import Hooks
import useGoToRoute from 'hooks/GoToRoute';
import useQueryParams from 'hooks/UseQueryParams';
// Import Helpers
import { generateTagComponentColor } from 'services/Helpers';
// Import Constants
import { ApprovalRequestStateColors, ApprovalRequestStateLabels } from 'constants/common';
import { RoutesList } from 'routes';

const { OBJECT_APPROVAL_REQUEST_VIEW } = RoutesList;

interface Props {
    approvalRequest: ApprovalRequest;
    isLink?: boolean;
}

const ApprovalStateTag: React.FC<Props> = ({ approvalRequest, isLink = false }) => {
    const { ApprovalRequestId, State } = approvalRequest;
    const { t } = useTranslation();
    const { search } = useLocation();
    const { getRoutePath } = useGoToRoute();
    const goToRouteQueryParams = useQueryParams();

    const approvalRequestPath = useMemo(
        () =>
            getRoutePath(
                OBJECT_APPROVAL_REQUEST_VIEW,
                { approvalRequestId: ApprovalRequestId },
                goToRouteQueryParams.encode({ backParams: search }),
            ),
        [ApprovalRequestId, getRoutePath, goToRouteQueryParams, search],
    );

    const Content = (
        <Tag
            name={t(ApprovalRequestStateLabels[State])}
            color={generateTagComponentColor(ApprovalRequestStateColors[State])}
            appearance="colored"
            cornerRadius="smooth-radius"
        />
    );

    return isLink ? <Link to={approvalRequestPath}>{Content}</Link> : Content;
};

export default memo(ApprovalStateTag);
